import { orderBy } from 'lodash'
import { useRouter } from 'next/router'
import React from 'react'
import { DATA_TEST_ID } from 'shared-constants/build/testIds'
import styled from 'styled-components'
import { EXPERT_BOOK_CONSULTATION_ID } from '@public/ExpertDetail/utils'
import { BUTTON_VARIANT, Button } from 'components/Button'
import { type Card } from 'components/Card'
import { GET_NOTIFIED_STEP } from 'components/GetNotifiedModal'
import Icon from 'components/Icon'
import NoData from 'components/NoData'
import { SessionBookingCard } from 'components/SessionBookingCard'
import { Tooltip } from 'components/Tooltip'
import { H4 } from 'components/Typography'
import { ROUTE } from 'constants/routes'
import { useCurrency } from 'providers/currency'
import {
  GA4_ECOM_CUSTOM_EVENTS,
  useGoogleAnalytics,
} from 'providers/googleAnalytics'
import { useUserAuth } from 'providers/userAuth'
import { SPACE } from 'Theme'
import {
  type CalculatedPrice,
  type PublicExpertDetailConsultationTabQuery,
} from 'types/graphql-generated'
import { getPriceByCurrencyIsoCodeFromCalculatedPrices } from 'utils/currency'
import { formatWithExpertUser } from 'utils/formatters'
import { silentPushUrl } from 'utils/pushUrl'
import { StyledCard } from './components'

const CardContent = styled.main`
  display: flex;
  flex-direction: column;
  gap: ${SPACE.PX_16};
`

interface SessionBookingCardListingMobileProps
  extends React.ComponentProps<typeof Card> {
  data: PublicExpertDetailConsultationTabQuery['expertBySlug']
  setGetNotifiedStep: React.Dispatch<React.SetStateAction<string>>
  setIsGetNotifiedModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  setIsBookingModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  setSelectedSession: React.Dispatch<
    React.SetStateAction<
      PublicExpertDetailConsultationTabQuery['expertBySlug']['sessions'][number] & {
        displayPrice?: CalculatedPrice
      }
    >
  >
}

export const SessionBookingCardListingMobile: React.FC<
  SessionBookingCardListingMobileProps
> = ({
  data,
  setGetNotifiedStep,
  setIsGetNotifiedModalOpen,
  setIsBookingModalOpen,
  setSelectedSession,
  ...props
}) => {
  const router = useRouter()
  const {
    isNoneSignedIn,
    isClientSignedIn,
    isExpertSignedIn,
    isAdminSignedIn,
    isPageLoading,
  } = useUserAuth()
  const selectedCurrency = useCurrency()
  const { executeDataToDataLayer } = useGoogleAnalytics()

  const isUserSignedInOrGuest =
    isNoneSignedIn || isClientSignedIn || isExpertSignedIn

  // Order so the available sessions are visible first
  const orderedSessions = orderBy(data.sessions, 'isAvailable', 'desc')

  return (
    <StyledCard
      id={EXPERT_BOOK_CONSULTATION_ID}
      isMobile
      as="article"
      display="flex"
      flexDirection="column"
      {...props}
    >
      {orderedSessions.length > 0 ? (
        <>
          <header>
            <H4 as="h3" mb={SPACE.PX_16}>
              Choose a consultation
            </H4>
          </header>
          <CardContent>
            {orderedSessions.map((session) => {
              const { title, firstName, lastName } = session.expertUser
              const displayPrice =
                getPriceByCurrencyIsoCodeFromCalculatedPrices(
                  selectedCurrency.currencyIsoCode,
                  session.price.calculatedPrices
                )
              const expertName = formatWithExpertUser({
                title,
                firstName,
                lastName,
                isWithPrefix: false,
              })
              let children = null
              if (isAdminSignedIn) {
                children = (
                  <Tooltip
                    body="Log out of your admin account to book a consultation"
                    placement="top"
                  >
                    <Button
                      mt={SPACE.PX_10}
                      variant={BUTTON_VARIANT.OUTLINE}
                      disabled
                      aria-label="booking disabled for admins"
                    >
                      {data.hasRequiredServicesActivated && session.isAvailable
                        ? 'Show availability'
                        : 'Request Availability'}
                    </Button>
                  </Tooltip>
                )
              } else if (isUserSignedInOrGuest && !isPageLoading) {
                if (data.hasRequiredServicesActivated && session.isAvailable) {
                  children = (
                    <Button
                      data-test-id={
                        DATA_TEST_ID.EXPERT_DETAIL_SHOW_AVAILABILITY_BTN
                      }
                      mt={SPACE.PX_10}
                      variant={BUTTON_VARIANT.OUTLINE}
                      onClick={() => {
                        setSelectedSession({ ...session, displayPrice })
                        setIsBookingModalOpen(true)
                        executeDataToDataLayer({
                          event:
                            GA4_ECOM_CUSTOM_EVENTS.VIEW_CALENDAR_CONSULTATIONS,
                          overrideData: {
                            session,
                          },
                          includeStoredData: false,
                        })
                      }}
                      aria-label="show availability"
                    >
                      Show availability
                    </Button>
                  )
                } else {
                  children = (
                    <Button
                      data-test-id={
                        DATA_TEST_ID.EXPERT_DETAIL_REQUEST_AVAILABILITY_BTN
                      }
                      mt={SPACE.PX_10}
                      variant={BUTTON_VARIANT.OUTLINE}
                      onClick={() => {
                        setSelectedSession(session)
                        setIsGetNotifiedModalOpen(true)
                      }}
                      aria-label="request availability"
                    >
                      Request Availability
                    </Button>
                  )
                }
              }
              return (
                <SessionBookingCard
                  key={session.id}
                  title={session.title}
                  description={session.description}
                  expertName={expertName}
                  duration={session.lengthInMinutes}
                  price={session.price}
                  displayPrice={displayPrice}
                >
                  {children}
                </SessionBookingCard>
              )
            })}
          </CardContent>
        </>
      ) : (
        <NoData
          icon={Icon.Communication}
          header={`${data.profileName} has no consultations currently available.`}
          content={`Subscribe and get notified via email once ${data.profileName} becomes available.`}
          button={() => (
            <Button
              data-test-id={
                DATA_TEST_ID.EXPERT_DETAIL_REQUEST_NEW_AVAILABILITY_BTN
              }
              mt={SPACE.PX_10}
              onClick={() => {
                setIsGetNotifiedModalOpen(true)
                setGetNotifiedStep(GET_NOTIFIED_STEP.SUBMIT)
                silentPushUrl({
                  router,
                  route: ROUTE.FIND_AN_EXPERT_SHOWROOM_TAB_DYNAMIC({
                    slug: data.slug,
                  }),
                  params: {
                    isGetNotifiedModalOpen: true,
                  },
                })
              }}
              aria-label="request new availability"
            >
              Request new availability
            </Button>
          )}
        />
      )}
    </StyledCard>
  )
}
